import { type IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import cx, { type ClassValue } from 'clsx';
import React, {
	forwardRef,
	useState,
} from 'react';

import { Div, Label } from '/react4xp/components/Element';
import { FontAwesomeIconWrapper } from '/react4xp/components/FontAwesomeIconWrapper';
import { customTwMerge } from '/react4xp/util/customTwMerge';

type InputAttributes = React.InputHTMLAttributes<HTMLInputElement>;

type ButtonRadioProps = Omit<InputAttributes, 'className'|'id'|'name'> & {
	error?: boolean;
	icon?: IconDefinition;
	iconColor?: FontAwesomeIconProps['color'];
	id: string;
	className?: ClassValue;
	label?: string;
	name: string;
};


export const ButtonRadio = forwardRef<HTMLInputElement, ButtonRadioProps>((
	{
		disabled,
		error,
		icon,
		iconColor,
		id,
		checked,
		className,
		label,
		name,
		onChange,
		onKeyDown: onKeyDownProp,
		onKeyUp: onKeyUpProp,
		value,
		...otherInputProps
	},
	inputRef,
) => {
	const [stateActive, setStateActive] = useState(false);

	const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
		if (event.code === 'Enter') {
			event.preventDefault(); // Avoid submitting the form
		}
		if (
			event.code === 'Enter'
			|| event.code === 'Space'
		) {
			setStateActive(true);
		}
		if (onKeyDownProp) onKeyDownProp(event);
	};

	const onKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
		if (
			event.code === 'Enter'
			|| event.code === 'Space'
		) {
			setStateActive(false);
		}
		if (onKeyUpProp) onKeyUpProp(event);
	};

	return (
		<div
			key={id}
		>
			<input
				{...otherInputProps}
				checked={checked}

				// className='peer sr-only' // No no, top of -1000px causes scrolling issue
				className='opacity-0 peer pointer-events-none size-0'

				disabled={disabled}
				id={id}
				name={name}
				onChange={onChange}
				onKeyDown={onKeyDown}
				onKeyUp={onKeyUp}
				ref={inputRef}
				type='radio'
				value={value}
			/>
			<Label
				htmlFor={id}
				className={customTwMerge(cx(
					stateActive
						? 'bg-surface-action-secondary-active'
						: checked
							? 'bg-surface-feedback-success'
							: 'bg-surface-action-secondary',
					checked ? 'text-stroke-thin' : undefined,
					checked ?
						disabled ? 'text-stroke-[#A6A6A6]' : 'text-stroke-[#002949]'
						: undefined,
					stateActive ? 'not-[:disabled]:scale-[0.98]' : undefined,
					'active:bg-surface-action-secondary-active',
					'active:not-[:disabled]:scale-[0.98]',
					'active:shadow-[inset_0_0_0_2px_rgb(2,81,105)]',
					disabled ? 'cursor-not-allowed' : 'cursor-pointer',
					'duration-200',
					'gap-xs',
					'hover:bg-surface-action-secondary-hover',
					'inline-flex',
					'items-center',
					'justify-center',
					'p-sm',
					'peer-focus-visible:outline-2',
					'peer-focus-visible:outline',
					'peer-focus-visible:outline-offset-2',
					{ 'pointer-events-none': disabled },
					'rounded-sm',
					'select-none',
					disabled ?
						stateActive || checked ? 'shadow-[inset_0_0_0_2px_rgb(166,166,166)]' :
							'shadow-[inset_0_0_0_1px_rgb(166,166,166)]' :
						stateActive || checked ? 'shadow-[inset_0_0_0_2px_rgb(2,81,105)]' :
							'shadow-[inset_0_0_0_1px_rgb(2,81,105)]',
					'outline-action-secondary',
					disabled ? 'text-neutral-disabled' : 'text-action-secondary',
					'transition-colors',
					className,
				))}
			>
				<Div
					className={[
						disabled ? 'bg-surface-action-disabled' : undefined,
						'border-sm',
						'border-solid',
						disabled ? 'border-action-disabled' :
							error ? 'border-feedback-error'
								: 'border-action-primary',
						'h-[var(--design-font-xl)]',
						'outline-action-primary',
						'peer-enabled:[&:hover:not(:has(input:disabled))]:border-md',
						'peer-focus-visible:outline-2',
						'peer-focus-visible:outline',
						'peer-focus-visible:outline-offset-2',
						'pointer-events-none',
						'relative',
						'rounded-[50%]',
						'w-[var(--design-font-xl)]',

						// Mark
						'after:content-[""]',
						'after:absolute',
						disabled ? 'after:bg-[#A6A6A6]' : // 'after:bg-surface-action-disabled' :
							error ? 'after:bg-[#A0002E]' : // 'after:bg-surface-feedback-error' :
								'after:bg-surface-action-primary',
						'after:h-[var(--design-font-xs)]',
						'after:left-1/2',
						checked ? undefined : 'after:opacity-0',
						'after:rounded-[50%]',
						'after:top-1/2',
						'after:-translate-x-1/2',
						'after:-translate-y-1/2',
						'after:transform',
						'after:w-[var(--design-font-xs)]',
					]}
					forceRender
				/>
				{label}
				{icon ? <FontAwesomeIconWrapper className={`text-xl ${iconColor}`} icon={icon}/> : null}
			</Label>
		</div>
	);
});
